import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Input,
  Row,
  Col,
  Badge,
  Spinner,
  Pagination,
  PaginationItem,
  PaginationLink,
  FormGroup,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import {
  FaHistory,
  FaSearch,
  FaFilter,
  FaDownload,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaAdjust,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaEye,
  FaCalendarAlt,
  FaInbox
} from 'react-icons/fa';
import { debounce } from 'lodash';
import { formatPrice } from '../../helpers/utility';
import MouvementStockDetails from './MouvementStockDetails';
import './MouvementStockPage.css';
import { mouvementStockActions } from '../../sagas/mouvementStockSlice';

const MouvementStockPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const {
    mouvements,
    loading,
    error,
    totalPages,
    totalElements,
    currentPage,
    pageSize
  } = useSelector(state => state.mouvementStock);

  // États locaux
  const [filters, setFilters] = useState({
    search: '',
    typeMouvement: '',
    articleId: '',
    numeroLot: '',
    dateDebut: '',
    dateFin: '',
    utilisateur: '',
    referenceDocument: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMouvement, setSelectedMouvement] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Types de mouvement pour le filtre
  const typesMouvement = [
    { value: '', label: t('mouvement.allTypes') },
    { value: 'ENTREE', label: t('mouvement.entree') },
    { value: 'SORTIE', label: t('mouvement.sortie') },
    { value: 'TRANSFERT', label: t('mouvement.transfert') },
    { value: 'AJUSTEMENT', label: t('mouvement.ajustement') },
    { value: 'INVENTAIRE', label: t('mouvement.inventaire') },
    { value: 'PERTE', label: t('mouvement.perte') },
    { value: 'RETOUR_CLIENT', label: t('mouvement.retourClient') }
  ];

  // Chargement initial
  useEffect(() => {
    loadMouvements();
  }, [currentPage]);

  // Recherche avec debounce
  const debouncedSearch = useCallback(
    debounce((searchFilters) => {
      dispatch(mouvementStockActions.searchRequest({
        ...searchFilters,
        page: 0,
        size: pageSize
      }));
    }, 500),
    [dispatch, pageSize]
  );

  const loadMouvements = () => {
    dispatch(mouvementStockActions.listRequest({
      ...filters,
      page: currentPage,
      size: pageSize
    }));
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    debouncedSearch(newFilters);
  };

  const handlePageChange = (page) => {
    dispatch(mouvementStockActions.setCurrentPage(page));
  };

  const resetFilters = () => {
    const emptyFilters = {
      search: '',
      typeMouvement: '',
      articleId: '',
      numeroLot: '',
      dateDebut: '',
      dateFin: '',
      utilisateur: '',
      referenceDocument: ''
    };
    setFilters(emptyFilters);
    dispatch(mouvementStockActions.listRequest({
      ...emptyFilters,
      page: 0,
      size: pageSize
    }));
  };

  const handleExport = () => {
    dispatch(mouvementStockActions.exportRequest(filters));
  };

  const getTypeMouvementIcon = (type) => {
    switch (type) {
      case 'ENTREE':
      case 'RETOUR_CLIENT':
        return <FaArrowUp className="text-success" />;
      case 'SORTIE':
      case 'PERTE':
        return <FaArrowDown className="text-danger" />;
      case 'TRANSFERT':
        return <FaExchangeAlt className="text-info" />;
      case 'AJUSTEMENT':
        return <FaAdjust className="text-warning" />;
      case 'INVENTAIRE':
        return <FaInbox className="text-primary" />;
      default:
        return <FaHistory className="text-secondary" />;
    }
  };

  const getTypeMouvementBadge = (type) => {
    const config = {
      'ENTREE': { color: 'success', label: t('mouvement.entree') },
      'SORTIE': { color: 'danger', label: t('mouvement.sortie') },
      'TRANSFERT': { color: 'info', label: t('mouvement.transfert') },
      'AJUSTEMENT': { color: 'warning', label: t('mouvement.ajustement') },
      'INVENTAIRE': { color: 'primary', label: t('mouvement.inventaire') },
      'PERTE': { color: 'dark', label: t('mouvement.perte') },
      'RETOUR_CLIENT': { color: 'success', label: t('mouvement.retourClient') }
    };

    const typeConfig = config[type] || { color: 'secondary', label: type };
    
    return (
      <Badge color={typeConfig.color} className="d-flex align-items-center">
        {getTypeMouvementIcon(type)}
        <span className="ms-1">{typeConfig.label}</span>
      </Badge>
    );
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = (mouvement) => {
    setSelectedMouvement(mouvement);
    setShowDetails(true);
  };

  if (loading && !mouvements.length) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-2 text-muted">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="mouvement-stock-page">
      <Card className="shadow-sm">
        <CardHeader className="bg-light">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-0 d-flex align-items-center">
                <FaHistory className="text-primary me-2" />
                {t('mouvement.title')}
              </h4>
            </Col>
            <Col xs="auto">
              <div className="d-flex gap-2">
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FaFilter className="me-1" />
                  {t('common.filters')}
                </Button>
                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={handleExport}
                  disabled={loading}
                >
                  <FaDownload className="me-1" />
                  {t('common.export')}
                </Button>
              </div>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          {/* Filtres */}
          {showFilters && (
            <Card className="mb-3 border-light">
              <CardBody className="bg-light">
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{t('common.search')}</Label>
                      <Input
                        type="text"
                        placeholder={t('mouvement.searchPlaceholder')}
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{t('mouvement.typeMouvement')}</Label>
                      <Input
                        type="select"
                        value={filters.typeMouvement}
                        onChange={(e) => handleFilterChange('typeMouvement', e.target.value)}
                      >
                        {typesMouvement.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{t('mouvement.dateDebut')}</Label>
                      <Input
                        type="date"
                        value={filters.dateDebut}
                        onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{t('mouvement.dateFin')}</Label>
                      <Input
                        type="date"
                        value={filters.dateFin}
                        onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{t('mouvement.numeroLot')}</Label>
                      <Input
                        type="text"
                        placeholder={t('mouvement.numeroLotPlaceholder')}
                        value={filters.numeroLot}
                        onChange={(e) => handleFilterChange('numeroLot', e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{t('mouvement.utilisateur')}</Label>
                      <Input
                        type="text"
                        placeholder={t('mouvement.utilisateurPlaceholder')}
                        value={filters.utilisateur}
                        onChange={(e) => handleFilterChange('utilisateur', e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{t('mouvement.referenceDocument')}</Label>
                      <Input
                        type="text"
                        placeholder={t('mouvement.referencePlaceholder')}
                        value={filters.referenceDocument}
                        onChange={(e) => handleFilterChange('referenceDocument', e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Button
                      color="outline-secondary"
                      onClick={resetFilters}
                      className="w-100"
                    >
                      {t('common.reset')}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Statistiques rapides */}
          <Row className="mb-3">
            <Col md={3}>
              <div className="card border-success">
                <div className="card-body p-3 text-center">
                  <FaArrowUp className="text-success h4 mb-2" />
                  <div className="text-xs text-uppercase text-success mb-1">
                    {t('mouvement.entrees')}
                  </div>
                  <div className="h5 mb-0">
                    {mouvements.filter(m => ['ENTREE', 'RETOUR_CLIENT'].includes(m.typeMouvement)).length}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="card border-danger">
                <div className="card-body p-3 text-center">
                  <FaArrowDown className="text-danger h4 mb-2" />
                  <div className="text-xs text-uppercase text-danger mb-1">
                    {t('mouvement.sorties')}
                  </div>
                  <div className="h5 mb-0">
                    {mouvements.filter(m => ['SORTIE', 'PERTE'].includes(m.typeMouvement)).length}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="card border-info">
                <div className="card-body p-3 text-center">
                  <FaExchangeAlt className="text-info h4 mb-2" />
                  <div className="text-xs text-uppercase text-info mb-1">
                    {t('mouvement.transferts')}
                  </div>
                  <div className="h5 mb-0">
                    {mouvements.filter(m => m.typeMouvement === 'TRANSFERT').length}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="card border-warning">
                <div className="card-body p-3 text-center">
                  <FaAdjust className="text-warning h4 mb-2" />
                  <div className="text-xs text-uppercase text-warning mb-1">
                    {t('mouvement.ajustements')}
                  </div>
                  <div className="h5 mb-0">
                    {mouvements.filter(m => ['AJUSTEMENT', 'INVENTAIRE'].includes(m.typeMouvement)).length}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Tableau des mouvements */}
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead className="table-light">
                <tr>
                  <th width="120">{t('mouvement.dateMouvement')}</th>
                  <th width="100">{t('mouvement.typeMouvement')}</th>
                  <th>{t('mouvement.article')}</th>
                  <th width="120">{t('mouvement.numeroLot')}</th>
                  <th width="80">{t('mouvement.quantite')}</th>
                  <th width="80">{t('mouvement.quantiteAvant')}</th>
                  <th width="80">{t('mouvement.quantiteApres')}</th>
                  <th width="100">{t('mouvement.prixUnitaire')}</th>
                  <th>{t('mouvement.motif')}</th>
                  <th width="100">{t('mouvement.utilisateur')}</th>
                  <th width="120">{t('mouvement.referenceDocument')}</th>
                  <th width="80">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {mouvements.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center text-muted py-4">
                      <FaHistory className="mb-2" size={24} />
                      <div>{t('mouvement.noMouvements')}</div>
                    </td>
                  </tr>
                ) : (
                  mouvements.map((mouvement) => (
                    <tr key={mouvement.id}>
                      <td className="text-center">
                        <small>{formatDateTime(mouvement.dateMouvement)}</small>
                      </td>
                      <td className="text-center">
                        {getTypeMouvementBadge(mouvement.typeMouvement)}
                      </td>
                      <td>
                        <div>
                          <strong>{mouvement.articleCode}</strong>
                          <br />
                          <small className="text-muted">{mouvement.articleNom}</small>
                        </div>
                      </td>
                      <td className="text-center">
                        {mouvement.gestionParLots ? (
                          <Badge color="info" className="text-wrap">
                            {mouvement.numeroLot}
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <strong className={
                          ['ENTREE', 'RETOUR_CLIENT'].includes(mouvement.typeMouvement) 
                            ? 'text-success' 
                            : ['SORTIE', 'PERTE'].includes(mouvement.typeMouvement)
                            ? 'text-danger'
                            : 'text-info'
                        }>
                          {['ENTREE', 'RETOUR_CLIENT'].includes(mouvement.typeMouvement) ? '+' : 
                           ['SORTIE', 'PERTE'].includes(mouvement.typeMouvement) ? '-' : ''}
                          {formatPrice(mouvement.quantite, false)}
                        </strong>
                      </td>
                      <td className="text-end">
                        {formatPrice(mouvement.quantiteAvant, false)}
                      </td>
                      <td className="text-end">
                        <strong>{formatPrice(mouvement.quantiteApres, false)}</strong>
                      </td>
                      <td className="text-end">
                        {mouvement.prixUnitaire ? (
                          <span>{formatPrice(mouvement.prixUnitaire)} DH</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {mouvement.motif || '-'}
                        </small>
                      </td>
                      <td className="text-center">
                        <Badge color="secondary" pill>
                          {mouvement.utilisateur || 'Système'}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {mouvement.referenceDocument ? (
                          <Badge color="outline-primary">
                            {mouvement.referenceDocument}
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          color="outline-info"
                          size="sm"
                          onClick={() => handleViewDetails(mouvement)}
                          title={t('common.viewDetails')}
                        >
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                {t('common.showing', {
                  start: currentPage * pageSize + 1,
                  end: Math.min((currentPage + 1) * pageSize, totalElements),
                  total: totalElements
                })}
              </small>
              <Pagination size="sm">
                <PaginationItem disabled={currentPage === 0}>
                  <PaginationLink first onClick={() => handlePageChange(0)}>
                    <FaAngleDoubleLeft />
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem disabled={currentPage === 0}>
                  <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)}>
                    <FaAngleLeft />
                  </PaginationLink>
                </PaginationItem>
                
                {/* Pages numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(0, Math.min(currentPage - 2 + i, totalPages - 1));
                  return (
                    <PaginationItem key={pageNum} active={pageNum === currentPage}>
                      <PaginationLink onClick={() => handlePageChange(pageNum)}>
                        {pageNum + 1}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem disabled={currentPage === totalPages - 1}>
                  <PaginationLink next onClick={() => handlePageChange(currentPage + 1)}>
                    <FaAngleRight />
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem disabled={currentPage === totalPages - 1}>
                  <PaginationLink last onClick={() => handlePageChange(totalPages - 1)}>
                    <FaAngleDoubleRight />
                  </PaginationLink>
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal de détails */}
      {showDetails && selectedMouvement && (
        <MouvementStockDetails
          mouvement={selectedMouvement}
          isOpen={showDetails}
          toggle={() => setShowDetails(false)}
        />
      )}
    </div>
  );
};

export default MouvementStockPage;
